import React, {
  FC,
  Fragment,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import { Formik } from 'formik';
import { find, get, isEqual, reduce } from 'lodash';
import * as yup from 'yup';
import Modal from '@broadleaf/admin-components/dist/common/components/Modal';
import SimpleModal from '@broadleaf/admin-components/dist/common/components/SimpleModal';
import useEventCallback from '@broadleaf/admin-components/dist/common/hooks/useEventCallback';
import useFormatMessage from '@broadleaf/admin-components/dist/common/hooks/useFormatMessage';
import Spinner from '@broadleaf/admin-components/dist/common/elements/Spinner';
import FormikError from '@broadleaf/admin-components/dist/form/components/FormikError';
import {
  clearFormikErrors,
  setFormikErrors
} from '@broadleaf/admin-components/dist/form/utils/RequestErrorHelpers';
import { request } from '@broadleaf/admin-components/dist/metadata/utils/request';
import { StatusChangeContext } from '@broadleaf/admin-components/dist/oms/contexts';
import { findOrderItem } from '@broadleaf/admin-components/dist/oms/utils/OrderItemUtils';
import type { IOrderFulfillment } from '@broadleaf/admin-components/dist/types/oms';
import { OrderFulfillmentStatus } from '@broadleaf/admin-components/dist/types/oms';

import auroraMessages from './AuroraFulfillmentCardStatusChange.messages';
import { AuroraStatusChangeFields } from 'app/oms/elements/StatusChangeFields';
import { FormattedMessage } from 'react-intl';
import { SVG } from '@broadleaf/admin-components/dist/common/components/SVG/SVG';
import { StatusChangeFields } from '@broadleaf/admin-components/dist/oms/elements';
import {
  FulfillmentCardStatusChangeModal,
  FulfillmentCardStatusChangeModalFooter
} from '@broadleaf/admin-components/dist/oms/components/ItemsOrderViewSection/components';
import messages from '@broadleaf/admin-components/dist/oms/components/ItemsOrderViewSection/components/FulfillmentCard/action-components/FulfillmentCardStatusChange/FulfillmentCardStatusChange.messages';

export const AuroraFulfillmentCardStatusChangeModal: FC<
  FulfillmentCardStatusChangeModal
> = props => {
  const {
    onClose,
    onSuccess,
    submitLabel,
    title,
    nextStatus,
    statusChangeReasons,
    ...rest
  } = props;
  const {
    contextParams,
    parent,
    fulfillmentItems,
    order,
    submitEndpoint,
    inventoryQuantities
  } = useContext(StatusChangeContext);
  const initialValues = useInitialValuesForFulfillmentCardStatusChange();
  const [showWarningModal, toggleWarningModal] = useState(false);
  const validationSchema = useValidationSchemaForFulfillmentCard(
    inventoryQuantities,
    nextStatus,
    parent.status,
    get(order, 'paymentGatewayType')
  );

  const formatMessage = useFormatMessage();

  const submit = useEventCallback(
    async (values, formikBag) => {
      // Check for stripe payment since it doesn't allow partial capture
      if (
        get(order, 'payments[0].gatewayType') === 'STRIPE' &&
        get(values, 'status') === 'FULFILLED' &&
        !isEqual(values, initialValues) &&
        !showWarningModal
      ) {
        toggleWarningModal(true);
      } else {
        formikBag.setSubmitting(true);
        try {
          const response = await request(
            {
              method: 'post',
              ...submitEndpoint,
              data: values
            },
            contextParams
          );

          // Send an extra request to cancel the items not captured
          if (
            get(order, 'payments[0].gatewayType') === 'STRIPE' &&
            get(values, 'status') === 'FULFILLED' &&
            !isEqual(values, initialValues)
          ) {
            const stripeCancelledValues = values;
            stripeCancelledValues.items = reduce(
              // todo Stripe migration
              initialValues.items,
              function (result, value, key) {
                if (value - values.items[key] !== 0) {
                  result[key] = value - values.items[key];
                }
                return result;
              },
              {}
            );
            stripeCancelledValues.status = 'CANCELLED';

            await request(
              {
                method: 'post',
                ...submitEndpoint,
                data: stripeCancelledValues
              },
              contextParams
            );
          }

          clearFormikErrors(formikBag);
          onSuccess(response);
          onClose();
        } catch (error) {
          setFormikErrors(error, formikBag);
        } finally {
          formikBag.setSubmitting(false);
        }
      }
    },
    [contextParams, onClose, onSuccess, submitEndpoint]
  );

  const handleSubmit = useCallback(
    async (values, formik) => {
      if (rest.handleSubmit) {
        return rest.handleSubmit(values, formik, submit);
      } else {
        // Aurora customization, do not use a confirm modal
        submit(values, formik);
        // End Aurora customization
      }
    },
    [formatMessage, submit]
  );

  const renderItem = useCallback(
    formik => (quantity, fulfillmentItemId) => {
      const quantityName = `items.${fulfillmentItemId}`;
      const reasonName = `reasons.${fulfillmentItemId}`;
      const fulfillmentItem = find(fulfillmentItems, {
        id: fulfillmentItemId
      });
      const orderItem = findOrderItem(order.orderItems, {
        id: fulfillmentItem.orderItemId
      });

      let maxQty;
      let inventoryQuantity;
      if (inventoryQuantities !== undefined) {
        inventoryQuantity = inventoryQuantities[orderItem?.sku];
        if (
          inventoryQuantity !== undefined &&
          inventoryQuantity < fulfillmentItem.quantity &&
          parent.status === 'PENDING_INVENTORY' &&
          nextStatus !== 'CANCELLED'
        ) {
          maxQty = inventoryQuantity;
        }
      } else {
        maxQty = fulfillmentItem.quantity;
      }

      return (
        <StatusChangeFields.Quantity
          formik={formik}
          key={fulfillmentItemId}
          quantityChangingStatusName={quantityName}
          reasonChangingStatusName={reasonName}
          orderItem={orderItem}
          itemQuantity={fulfillmentItem.quantity}
          maxQuantity={maxQty}
          unitMerchandisePrice={fulfillmentItem.unitMerchandisePrice}
          quantityChangingStatusValue={
            inventoryQuantity !== undefined && quantity > maxQty
              ? maxQty
              : quantity
          }
          statusChangeReasons={statusChangeReasons}
          currentStatus={parent.status}
        />
      );
    },
    [fulfillmentItems, order.orderItems]
  );

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {formik => (
        <>
          <SimpleModal
            closeOnClickOutside={false}
            header={
              <Fragment>
                <Modal.Header.Title>{title}</Modal.Header.Title>
                <FormikError className="tw-ml-2 tw-mr-auto" formik={formik} />
                {onClose && <Modal.Close onClose={onClose} />}
              </Fragment>
            }
            footer={
              <FulfillmentCardStatusChangeModalFooter
                formik={formik}
                submitLabel={submitLabel}
              />
            }
            isOpen
            onClose={onClose}
            size="lg"
            title={title}
          >
            <AuroraStatusChangeFields
              formik={formik}
              renderItem={renderItem}
              paymentGatewayType={get(order, 'paymentGatewayType')}
              fleetTaxExempt={get(order, 'attributes.FLEET_TAX_EXEMPT', false)}
            />
          </SimpleModal>
          <FulfillStripeWarningModal
            onClose={onClose}
            isOpen={showWarningModal}
            toggleWarningModal={toggleWarningModal}
            formik={formik}
          />
        </>
      )}
    </Formik>
  );
};

const FulfillStripeWarningModal = ({
  onClose,
  isOpen,
  toggleWarningModal,
  formik
}) => {
  const formatMessage = useFormatMessage();
  return (
    <SimpleModal
      closeOnClickOutside={false}
      header={
        <FulfillStripeWarningModalHeader
          title={formatMessage(auroraMessages.stripePartialWarningTitle)}
          onClose={onClose}
        />
      }
      footer={
        <FulfillStripeWarningModalFooter
          onClose={onClose}
          toggleWarningModal={toggleWarningModal}
          formik={formik}
        />
      }
      body={
        <div className="tw-rounded tw-border tw-border-solid tw-border-yellow-400 tw-bg-yellow-100 tw-px-2 tw-py-1 tw-text-sm tw-leading-snug tw-text-yellow-700 md:tw-px-4 md:tw-py-2 lg:tw-text-base lg:tw-leading-normal">
          <FormattedMessage
            {...auroraMessages.stripePartialWarning}
            values={{
              breakLine: <br />,
              breakLineWide: <div className={'tw-p-1'} />,
              important: (
                <b className={'tw-text-red-500'}>
                  {formatMessage(auroraMessages.stripePartialWarningImportant)}
                </b>
              )
            }}
          />
        </div>
      }
      isOpen={isOpen}
      onClose={() => {
        toggleWarningModal(false);
      }}
      size="xl"
      title={formatMessage(auroraMessages.stripePartialWarningTitle)}
    />
  );
};

const FulfillStripeWarningModalFooter = ({
  toggleWarningModal,
  formik,
  onClose
}) => {
  const formatMessage = useFormatMessage();
  return (
    <>
      {formik.isSubmitting ? (
        <Spinner className="tw-mr-2" />
      ) : (
        <FormikError className="tw-mr-2" formik={formik} />
      )}

      <button
        className="focus:tw-shadow-outline tw-w-full tw-rounded tw-border tw-bg-white tw-px-4 tw-py-4 tw-text-sm tw-font-semibold tw-text-gray-700 tw-shadow hover:tw-bg-secondary-100 hover:tw-text-gray-900 focus:tw-bg-secondary-100 focus:tw-text-gray-900 focus:tw-outline-none md:tw-w-auto md:tw-py-2"
        onClick={() => {
          toggleWarningModal(false);
        }}
      >
        {formatMessage(auroraMessages.stripePartialWarningCancel)}
      </button>
      <button
        className="focus:tw-shadow-outline tw-w-full tw-rounded tw-border tw-bg-primary-700 tw-px-4 tw-py-4 tw-text-sm tw-font-semibold tw-text-primary-100 tw-shadow hover:tw-bg-primary-800 focus:tw-outline-none md:tw-w-auto md:tw-py-2"
        onClick={() => {
          formik.submitForm();
        }}
        type="submit"
      >
        {formatMessage(auroraMessages.stripePartialWarningContinue)}
      </button>
    </>
  );
};

const FulfillStripeWarningModalHeader = ({ title, onClose }) => {
  return (
    <Fragment>
      <div className="tw-inline-flex tw-items-center tw-p-2 tw-text-sm tw-text-yellow-700">
        <SVG
          className="tw-mr-2 tw-inline tw-h-4 tw-w-4 tw-fill-current"
          name="exclamation-outline"
        />
        <Modal.Header.Title>
          <div className="tw-text-yellow-700">{title}</div>
        </Modal.Header.Title>
      </div>
      {onClose && <Modal.Close onClose={onClose} />}
    </Fragment>
  );
};

type IAuroraFulfillmentStatusChangeRequest = {
  fulfillmentId: string;
  status: OrderFulfillmentStatus;
  items?: Record<string, number>;
  itemQuantities?: Record<string, number>;
  purchaseOrderNumber?: string;
  dealerInvoiceNumber?: string;
};

/**
 * Hook that returns the initial form values for the status change form.
 *
 * @returns {{fulfillmentId: string, items: { [string]: number }, status: string}}
 */
function useInitialValuesForFulfillmentCardStatusChange(): IAuroraFulfillmentStatusChangeRequest {
  const { parent, nextStatus, order } = useContext(StatusChangeContext);
  const fulfillment = parent as IOrderFulfillment;
  const purchaseOrderNumber = get(order, 'purchaseOrderNumber');
  const dealerInvoiceNumber = get(order, 'dealerInvoiceNumber');
  return useMemo(() => {
    return {
      fulfillmentId: fulfillment.id,
      status: nextStatus,
      purchaseOrderNumber: purchaseOrderNumber,
      dealerInvoiceNumber: dealerInvoiceNumber,
      taxOverride: get(fulfillment, 'taxTotal.amount', 0.0),
      items: reduce(
        fulfillment.fulfillmentItems,
        (result, fulfillmentItem) => {
          return {
            ...result,
            [fulfillmentItem.id]: fulfillmentItem.quantity
          };
        },
        {}
      )
    };
  }, [
    fulfillment.fulfillmentItems,
    fulfillment.id,
    nextStatus,
    dealerInvoiceNumber,
    purchaseOrderNumber
  ]);
}

/**
 * Hooks that returns the validation schema for the status change form.
 *
 * @returns {ObjectSchema<Shape<object | undefined, object>>}
 */
function useValidationSchemaForFulfillmentCard(
  inventoryQuantities: Record<string, number>,
  nextStatus: string,
  currentStatus: string,
  paymentGatewayType: string
): yup.ObjectSchema {
  const { fulfillmentItems } = useContext(StatusChangeContext);
  const formatMessage = useFormatMessage();
  const minMessage = formatMessage(messages.itemQuantityMinimum);
  const maxMessage = formatMessage(messages.itemQuantityMaximum);
  const requiredMessage = formatMessage(messages.itemQuantityRequired);
  const dealerInvoiceRequiredMessage = formatMessage(
    auroraMessages.dealerInvoiceNumberRequired
  );
  const purchaseOrderRequiredMessage = formatMessage(
    auroraMessages.purchaseOrderNumberRequired
  );

  const isCapturingAuroraPay = useMemo(() => {
    return isCaptureAuroraPay(paymentGatewayType, status);
  }, [paymentGatewayType, status]);

  return useMemo(() => {
    return yup.object().shape({
      fulfillmentId: yup.string(),
      status: yup.string(),
      dealerInvoiceNumber: isCapturingAuroraPay
        ? yup
            .string()
            .required(dealerInvoiceRequiredMessage)
            .trim()
            .min(1, 'Field cannot be blank or contain only whitespace')
        : yup.string(),
      taxOverride: yup.number(),
      purchaseOrderNumber: isCapturingAuroraPay
        ? yup
            .string()
            .required(purchaseOrderRequiredMessage)
            .trim()
            .min(1, 'Field cannot be blank or contain only whitespace')
        : yup.string(),
      items: yup.object().shape(
        reduce(
          fulfillmentItems,
          (result, fulfillmentItem) => {
            return {
              ...result,
              [fulfillmentItem.id]: yup
                .number()
                .nullable()
                .transform(emptyStringToNull)
                .min(0, minMessage)
                .max(
                  inventoryQuantities !== undefined &&
                    inventoryQuantities[
                      fulfillmentItem?.internalAttributes['sku']
                    ] !== undefined &&
                    nextStatus !== 'CANCELLED' &&
                    currentStatus === 'PENDING_INVENTORY'
                    ? inventoryQuantities[
                        fulfillmentItem?.internalAttributes['sku']
                      ]
                    : fulfillmentItem.quantity,
                  maxMessage
                )
                .required(requiredMessage)
            };
          },
          {}
        )
      )
    });
  }, [
    fulfillmentItems,
    maxMessage,
    minMessage,
    requiredMessage,
    isCapturingAuroraPay,
    dealerInvoiceRequiredMessage,
    purchaseOrderRequiredMessage
  ]);
}

function isCaptureAuroraPay(paymentGatewayType, nextStatus) {
  return (
    paymentGatewayType === 'CORCENTRIC' && nextStatus === 'CAPTURING_PAYMENT'
  );
}

function emptyStringToNull(value: string, originalValue: string) {
  if (typeof originalValue === 'string' && originalValue === '') {
    return null;
  }
  return value;
}
